import React from "react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { X, Waves, UserPlus, Calendar as CalendarIcon, Search, Check, Users, Clock, Undo2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { ScrollArea } from "../../components/ui/scroll-area";
import LessonQRCode from "../../pages/LessonQRCode";

/**
 * Modal for viewing lesson details, managing enrollments and attendance.
 */
const LessonDetailModal = ({
  open,
  onOpenChange,
  selectedLesson,
  lessonEnrollments,
  loadingEnrollments,
  // Add student panel
  showAddStudentPanel,
  setShowAddStudentPanel,
  addStudentSearch,
  setAddStudentSearch,
  addStudentResults,
  addStudentLoading,
  // Inline student creation
  showDetailStudentCreate,
  setShowDetailStudentCreate,
  detailNewStudent,
  setDetailNewStudent,
  // Actions
  onCancelLesson,
  onAddStudentToLesson,
  onCreateAndAddStudent,
  onMarkAttendance,
  onMarkAbsent,
  onRevertAttendance
}) => {
  if (!selectedLesson) return null;

  const getLessonTitle = () => {
    if (selectedLesson.lesson_category === "surf") return "Surf";
    if (selectedLesson.lesson_category === "bodyboard") return "Bodyboard";
    return "Surf & Bodyboard";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Waves className="h-5 w-5 text-[#6697a4]" />
            {getLessonTitle()}
            {selectedLesson.lesson_type === "private" && (
              <Badge className="bg-[#FF7F50] text-white border-0 text-xs">Privada</Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Lesson Info */}
          <div className="p-3 bg-slate-50 rounded-lg space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-sm text-[#475569]">
                <CalendarIcon className="h-4 w-4" />
                {selectedLesson.date && format(parseISO(selectedLesson.date), "EEEE, d 'de' MMMM", { locale: pt })}
              </div>
              <div className="flex items-center gap-2 text-sm text-[#475569]">
                <Clock className="h-4 w-4" />
                {selectedLesson.start_time} - {selectedLesson.end_time}
              </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-sm text-[#475569]">
                <Users className="h-4 w-4" />
                {selectedLesson.enrolled_count || 0}/{selectedLesson.max_students} alunos
              </div>
              {selectedLesson.lesson_type !== "private" && (
                <span className="text-lg font-bold text-[#6697a4]">{selectedLesson.price}€</span>
              )}
            </div>
            {selectedLesson.teachers_data?.length > 0 && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-[#94A3B8]">Professores:</span>
                <div className="flex -space-x-2">
                  {selectedLesson.teachers_data.map(t => (
                    <Avatar key={t.user_id} className="h-6 w-6 border-2 border-white" title={t.name}>
                      <AvatarImage src={t.picture} />
                      <AvatarFallback className="text-[8px] bg-[#6697a4] text-white">{t.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                  ))}
                </div>
                <span className="text-xs text-[#475569]">{selectedLesson.teachers_data.map(t => t.name).join(", ")}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <LessonQRCode 
              lessonId={selectedLesson.lesson_id} 
              lessonTitle={getLessonTitle()} 
            />
            <Button 
              size="sm" 
              variant="outline" 
              className="text-[#6697a4] border-[#6697a4]/30 hover:bg-[#6697a4]/5"
              onClick={() => setShowAddStudentPanel(!showAddStudentPanel)}
              data-testid="detail-add-student-btn"
            >
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Adicionar Aluno
            </Button>
            {selectedLesson.status !== "cancelled" && (
              <Button 
                size="sm" 
                variant="outline" 
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={onCancelLesson}
                data-testid="detail-cancel-lesson-btn"
              >
                <X className="h-3.5 w-3.5 mr-1" /> Cancelar Aula
              </Button>
            )}
          </div>

          {/* Add Student Panel */}
          {showAddStudentPanel && (
            <div className="p-3 bg-[#6697a4]/5 rounded-lg border border-[#6697a4]/10" data-testid="detail-add-student-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#475569]">Adicionar aluno à aula</span>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 text-xs text-[#6697a4]" 
                    onClick={() => setShowDetailStudentCreate(!showDetailStudentCreate)}
                    data-testid="detail-toggle-create-student"
                  >
                    <UserPlus className="h-3 w-3 mr-1" /> Criar Novo
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowAddStudentPanel(false)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Inline create new student */}
              {showDetailStudentCreate && (
                <div className="mb-3 p-3 bg-white rounded-lg border border-slate-200 space-y-2">
                  <Input 
                    placeholder="Nome *" 
                    value={detailNewStudent.name} 
                    onChange={(e) => setDetailNewStudent({...detailNewStudent, name: e.target.value})} 
                    className="h-8 text-sm" 
                    data-testid="detail-new-student-name" 
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input 
                      placeholder="Email (opcional)" 
                      value={detailNewStudent.email} 
                      onChange={(e) => setDetailNewStudent({...detailNewStudent, email: e.target.value})} 
                      className="h-8 text-sm" 
                      data-testid="detail-new-student-email" 
                    />
                    <Input 
                      placeholder="Telefone (opcional)" 
                      value={detailNewStudent.phone} 
                      onChange={(e) => setDetailNewStudent({...detailNewStudent, phone: e.target.value})} 
                      className="h-8 text-sm" 
                      data-testid="detail-new-student-phone" 
                    />
                  </div>
                  <Button 
                    size="sm" 
                    onClick={onCreateAndAddStudent} 
                    disabled={addStudentLoading}
                    className="bg-[#6697a4] hover:bg-[#567f8a] h-7 text-xs" 
                    data-testid="detail-create-add-student-btn"
                  >
                    {addStudentLoading ? "A criar..." : "Criar e Adicionar à Aula"}
                  </Button>
                </div>
              )}

              {/* Search existing students */}
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
                <Input
                  placeholder="Pesquisar aluno por nome ou email..."
                  value={addStudentSearch}
                  onChange={(e) => setAddStudentSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                  data-testid="detail-add-student-search"
                />
              </div>
              {addStudentResults.length > 0 && (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {addStudentResults.map(s => (
                    <button
                      key={s.user_id}
                      onClick={() => onAddStudentToLesson(s.user_id)}
                      disabled={addStudentLoading}
                      className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-white transition-colors text-left"
                      data-testid={`detail-add-student-option-${s.user_id}`}
                    >
                      <Avatar className="h-7 w-7 shrink-0">
                        <AvatarImage src={s.picture} />
                        <AvatarFallback className="bg-[#6697a4] text-white text-[10px]">{s.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#0F172A] truncate">{s.name || s.email}</p>
                        <p className="text-[10px] text-[#94A3B8]">{s.credits || 0} créditos grupo · {s.private_credits || 0} privados</p>
                      </div>
                      <UserPlus className="h-4 w-4 text-[#6697a4] shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              {addStudentSearch.trim() && addStudentResults.length === 0 && (
                <p className="text-xs text-[#94A3B8] text-center py-2">Nenhum aluno encontrado</p>
              )}
            </div>
          )}

          {/* Enrolled Students */}
          <div>
            <h4 className="font-semibold text-[#3d5a63] mb-3 flex items-center gap-2">
              <Users className="h-4 w-4" /> Alunos Inscritos ({lessonEnrollments.length})
            </h4>
            
            {loadingEnrollments ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-2 border-[#6697a4] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : lessonEnrollments.length === 0 ? (
              <p className="text-sm text-[#94A3B8] py-4 text-center">Nenhum aluno inscrito nesta aula.</p>
            ) : (
              <ScrollArea className="max-h-[300px]">
                <div className="space-y-2 pr-2">
                  {lessonEnrollments.map(enrollment => (
                    <EnrollmentCard 
                      key={enrollment.enrollment_id}
                      enrollment={enrollment}
                      onMarkAttendance={onMarkAttendance}
                      onMarkAbsent={onMarkAbsent}
                      onRevertAttendance={onRevertAttendance}
                    />
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
        
        <DialogFooter className="border-t pt-3 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Individual enrollment card component
 */
const EnrollmentCard = ({ enrollment, onMarkAttendance, onMarkAbsent, onRevertAttendance }) => {
  const getStatusBadge = () => {
    const statusMap = {
      attended: { label: "Presente", className: "bg-green-100 text-green-700 border-0" },
      absent: { label: "Faltou", className: "bg-red-100 text-red-700 border-0" },
      reserved: { label: "Reservado", className: "bg-amber-100 text-amber-700 border-0" },
      default: { label: "Inscrito", className: "bg-blue-100 text-blue-700 border-0" }
    };
    return statusMap[enrollment.status] || statusMap.default;
  };

  const status = getStatusBadge();

  return (
    <div className="p-3 bg-slate-50 rounded-lg" data-testid={`detail-enrollment-${enrollment.enrollment_id}`}>
      <div className="flex items-center gap-3">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarImage src={enrollment.user?.picture} />
          <AvatarFallback className="bg-[#6697a4] text-white text-xs">{enrollment.user?.name?.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-[#3d5a63] truncate">
              {enrollment.user?.name || enrollment.user?.email}
            </span>
            <Badge className={`shrink-0 text-xs ${status.className}`}>
              {status.label}
            </Badge>
          </div>
          {enrollment.activity_type && (
            <Badge variant="outline" className="text-[10px] mt-1">
              {enrollment.activity_type === "surf" ? "Surf" : "Bodyboard"}
            </Badge>
          )}
        </div>
      </div>
      
      {/* Attendance Actions */}
      {!["attended", "absent", "cancelled"].includes(enrollment.status) && (
        <div className="flex items-center gap-2 flex-wrap mt-2 ml-11">
          <Button 
            size="sm" 
            variant="outline" 
            className="text-green-600 border-green-300 hover:bg-green-50 h-7 text-xs" 
            onClick={() => onMarkAttendance(enrollment.enrollment_id)}
            data-testid={`detail-mark-attendance-${enrollment.enrollment_id}`}
          >
            <Check className="h-3 w-3 mr-1" /> Presente
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="text-red-500 border-red-200 hover:bg-red-50 h-7 text-xs" 
            onClick={() => onMarkAbsent(enrollment.enrollment_id, false)}
            data-testid={`detail-mark-absent-${enrollment.enrollment_id}`}
          >
            <X className="h-3 w-3 mr-1" /> Faltou
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            className="text-amber-600 hover:bg-amber-50 h-7 text-xs" 
            onClick={() => onMarkAbsent(enrollment.enrollment_id, true)}
            data-testid={`detail-mark-absent-refund-${enrollment.enrollment_id}`}
          >
            Faltou + Devolver
          </Button>
        </div>
      )}
      
      {/* Revert Action */}
      {["attended", "confirmed", "absent"].includes(enrollment.status) && (
        <div className="flex items-center gap-2 mt-2 ml-11">
          <Button 
            size="sm" 
            variant="ghost" 
            className="text-[#6697a4] hover:bg-[#6697a4]/10 h-7 text-xs" 
            onClick={() => onRevertAttendance(enrollment.enrollment_id)}
            data-testid={`detail-revert-attendance-${enrollment.enrollment_id}`}
          >
            <Undo2 className="h-3 w-3 mr-1" /> Reverter
          </Button>
        </div>
      )}
    </div>
  );
};

export default LessonDetailModal;
